import React, { useState, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import values from 'lodash/values';

import {
  FullScreenOverlayBody,
  FullScreenOverlayHeader,
  FullScreenOverlayContent
} from '@bibliocommons/base-full-screen-overlay';
import Tabs from '@bibliocommons/base-tabs';
import { TableStencil } from '@bibliocommons/table';
import { catalogBibShape } from '@bibliocommons/bc-prop-types';
import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';
import {
  selectDraftLists,
  selectPublishedLists,
  selectDraftListsPagination,
  selectPublishedListsPagination,
  selectDraftListsLoading,
  selectPublishedListsLoading
} from 'app/selectors/ListsSelector';
import ListsActions from 'app/actions/ListsActions';
import { LIST_CATEGORIES } from 'app/constants/ListsConstants';
import AddToListTable from './AddToListTable';

import './AddToListOverlay.scss';

const TAB_NAMES = {
  DRAFT: 'draft',
  PUBLISHED: 'published'
};

export default function AddToListOverlay({ catalogBib }) {
  const metadataId = catalogBib.get('metadataId');
  const [selectedTab, setSelectedTab] = useState(TAB_NAMES.DRAFT);
  const dispatch = useDispatch();
  const intl = useIntl();

  const draftLists = useSelector(selectDraftLists);
  const publishedLists = useSelector(selectPublishedLists);
  const draftListsPagination = useSelector(selectDraftListsPagination);
  const publishedListsPagination = useSelector(selectPublishedListsPagination);
  const draftListsLoading = useSelector(selectDraftListsLoading);
  const publishedListsLoading = useSelector(selectPublishedListsLoading);

  const initialDraftListsLoading = draftLists.size === 0 && draftListsLoading;
  const initialPublishedListsLoading = publishedLists.size === 0 && publishedListsLoading;

  const counts = {
    [TAB_NAMES.DRAFT]: draftListsPagination.get('count', 0),
    [TAB_NAMES.PUBLISHED]: publishedListsPagination.get('count', 0)
  };

  const tabOptions = values(TAB_NAMES).map(tabName => ({
    label: intl.formatMessage({ id: `my_${tabName}_lists` }, { count: counts[tabName] }),
    selected: selectedTab === tabName,
    value: tabName
  }));

  useEffect(() => {
    batch(() => {
      dispatch(ListsActions.getCurrentUserLists({ metadataId, category: LIST_CATEGORIES.DRAFT_NOT_PUBLISHED }));
      dispatch(ListsActions.getCurrentUserLists({ metadataId, category: LIST_CATEGORIES.PUBLISHED }));
    });
  }, [dispatch, catalogBib, metadataId]);

  function handlePageClick(page) {
    const category = selectedTab === TAB_NAMES.DRAFT ? LIST_CATEGORIES.DRAFT_NOT_PUBLISHED : LIST_CATEGORIES.PUBLISHED;
    dispatch(ListsActions.getCurrentUserLists({ metadataId, page, category }));
  }

  function renderEmptyContent() {
    return (
      <div className="empty-content">
        <FormattedMessage id={`empty_${selectedTab}_lists`} />
      </div>
    );
  }

  function renderContent() {
    switch (selectedTab) {
      case TAB_NAMES.DRAFT: {
        return (
          <AddToListTable
            handlePageClick={handlePageClick}
            renderEmptyContent={renderEmptyContent}
            lists={draftLists}
            isFetching={draftListsLoading}
            pagination={draftListsPagination}
            catalogBib={catalogBib}
          />
        );
      }

      case TAB_NAMES.PUBLISHED: {
        return (
          <AddToListTable
            handlePageClick={handlePageClick}
            renderEmptyContent={renderEmptyContent}
            lists={publishedLists}
            isFetching={publishedListsLoading}
            pagination={publishedListsPagination}
            catalogBib={catalogBib}
          />
        );
      }

      default:
        return null;
    }
  }

  return (
    <FullScreenOverlayBody className="cp-add-to-list-overlay">
      <FullScreenOverlayHeader title={<FormattedMessage id="add_to_list" />} />
      <FullScreenOverlayContent>
        <BibBriefContainer metadataId={metadataId} />
        {initialDraftListsLoading || initialPublishedListsLoading ? (
          <TableStencil />
        ) : (
          <Tabs options={tabOptions} renderContent={renderContent} handleSelect={setSelectedTab} />
        )}
      </FullScreenOverlayContent>
    </FullScreenOverlayBody>
  );
}

AddToListOverlay.propTypes = {
  catalogBib: catalogBibShape.isRequired
};
